/* eslint-disable no-console */
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Typography,
  Box,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  Switch,
  FormControlLabel
} from "@mui/material";
import { Map } from "@mui/icons-material";
import { functions, firestore, auth, firebaseProjectId } from "../services/firebase";
import QueryHistoryCard from "../components/OSMDataAdmin/QueryHistoryCard";
import CollectedDataCard from "../components/OSMDataAdmin/CollectedDataCard";
import DataCollectionInfo from "../components/OSMDataAdmin/DataCollectionInfo";
import SearchConfigurationCard from "../components/OSMDataAdmin/SearchConfigurationCard";
import FetchProgressAlert from "../components/OSMDataAdmin/FetchProgressAlert";
import FetchErrorAlert from "../components/OSMDataAdmin/FetchErrorAlert";
import CollectionResultsCard from "../components/OSMDataAdmin/CollectionResultsCard";
import ConflictResolutionAlert from "../components/OSMDataAdmin/ConflictResolutionAlert";
import ConflictsSummary from "../components/OSMDataAdmin/ConflictsSummary";
import ConflictsTable from "../components/OSMDataAdmin/ConflictsTable";
import ConflictActions from "../components/OSMDataAdmin/ConflictActions";
import PushInfoAlert from "../components/OSMDataAdmin/PushInfoAlert";
import PushCompleteAlert from "../components/OSMDataAdmin/PushCompleteAlert";
import UploadSummaryCard from "../components/OSMDataAdmin/UploadSummaryCard";
import BarsReviewTable from "../components/OSMDataAdmin/BarsReviewTable";
import PushActions from "../components/OSMDataAdmin/PushActions";
import ClearDataDialog from "../components/OSMDataAdmin/ClearDataDialog";
import DeleteRowsDialog from "../components/OSMDataAdmin/DeleteRowsDialog";
import { Container } from "../components/OSMDataAdmin/styled";
import { useUserRoles } from "../globalstore";
import { useFetchRegions, useRegions } from "../actions/regions";

const METERS_IN_MILE = 1609.344;
const DEFAULT_RADIUS_METERS = 5000;
const MIN_RADIUS_METERS = 100;
const MAX_RADIUS_METERS = 50000;

export function OSMDataAdmin() {
  const [location, setLocation] = useState("");
  const [radius, setRadius] = useState(DEFAULT_RADIUS_METERS);
  const [clearExistingData, setClearExistingData] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateQuery, setDuplicateQuery] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [progressCount, setProgressCount] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [functionTimeout, setFunctionTimeout] = useState(false);
  const [osmRawData, setOsmRawData] = useState([]);
  const [loadingRawData, setLoadingRawData] = useState(false);
  const [osmQueries, setOsmQueries] = useState([]);
  const [loadingQueries, setLoadingQueries] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [conflicts, setConflicts] = useState([]);
  const [loadingConflicts, setLoadingConflicts] = useState(false);
  const [fieldSelections, setFieldSelections] = useState({}); // {conflictId: {fieldName: 'new' | 'existing'}}
  const [barsToExclude, setBarsToExclude] = useState(new Set()); // Set of OSM record IDs to exclude from push
  const [pushingToDatabase, setPushingToDatabase] = useState(false);
  const [pushComplete, setPushComplete] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [activeQueryId, setActiveQueryId] = useState(null);
  const [conflictsError, setConflictsError] = useState(null);
  const [assignRegionId, setAssignRegionId] = useState("");
  const [addUnlistedOnly, setAddUnlistedOnly] = useState(false);
  const [blacklistUnincluded, setBlacklistUnincluded] = useState(false);
  const [skipBlacklistOnDelete, setSkipBlacklistOnDelete] = useState(false);

  const radiusMiles = useMemo(() => Number((radius / METERS_IN_MILE).toFixed(2)), [radius]);
  const conflictIdSet = useMemo(() => new Set(conflicts.map((c) => c.osmRecordId)), [conflicts]);

  const filteredOsmRawData = useMemo(() => {
    if (!addUnlistedOnly) {
      return osmRawData;
    }
    return osmRawData.filter((bar) => !conflictIdSet.has(bar.id));
  }, [addUnlistedOnly, conflictIdSet, osmRawData]);
  
  const unsubscribeRef = useRef(null);
  const startTimeRef = useRef(null);
  const rawDataUnsubscribeRef = useRef(null);
  const queriesUnsubscribeRef = useRef(null);

  const { userRoles: { isAdmin } } = useUserRoles();
  const regions = useRegions();
  const { fetchRegions } = useFetchRegions();

  const osmDataFunction = functions.httpsCallable("fetchOSMData");
  const fetchConflictsFunction = functions.httpsCallable("fetchSupabaseOSMConflicts");
  const pushBarsFunction = functions.httpsCallable("pushOSMBarsToDatabase");
  const addToBlacklistFunction = functions.httpsCallable("addOSMBarsToBlacklist");

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      if (rawDataUnsubscribeRef.current) {
        rawDataUnsubscribeRef.current();
      }
      if (queriesUnsubscribeRef.current) {
        queriesUnsubscribeRef.current();
      }
    };
  }, []);

  // Load existing OSM raw data on component mount and keep it updated
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return;
    }

    setLoadingRawData(true);
    
    // Set up real-time listener for OSM raw data
    rawDataUnsubscribeRef.current = firestore
      .collection("raw_osm_data")
      .where("created_by_user", "==", currentUser.uid)
      .orderBy("firebase_created_at", "desc")
      .onSnapshot((snapshot) => {
        const rawData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log(`Loaded ${rawData.length} OSM raw data records`);
        setOsmRawData(rawData);
        setLoadingRawData(false);
      }, (error) => {
        console.error("Error loading OSM raw data:", error);
        setLoadingRawData(false);
      });

    // Cleanup function
    return () => {
      if (rawDataUnsubscribeRef.current) {
        rawDataUnsubscribeRef.current();
      }
    };
  }, []);

  // Load existing OSM queries on component mount and keep them updated
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.log("No current user, skipping OSM queries listener");
      return;
    }

    console.log("Setting up OSM queries listener for user:", currentUser.uid);
    setLoadingQueries(true);
    
    // Set up real-time listener for OSM queries
    let queryRef = firestore.collection("osm_queries");

    if (isAdmin) {
      queryRef = queryRef.orderBy("query_date", "desc");
    } else {
      queryRef = queryRef
        .where("created_by_user", "==", currentUser.uid)
        .orderBy("query_date", "desc");
    }

    queriesUnsubscribeRef.current = queryRef
      .onSnapshot((snapshot) => {
        console.log("OSM queries snapshot received, docs count:", snapshot.docs.length);
        const queries = snapshot.docs.map(doc => {
          const data = doc.data();
          console.log("Query doc:", doc.id, data);
          return {
            id: doc.id,
            ...data
          };
        });
        
        console.log(`Loaded ${queries.length} OSM query records`, queries);
        setOsmQueries(queries);
        setLoadingQueries(false);
      }, (error) => {
        console.error("Error loading OSM queries:", error);
        console.error("Error code:", error.code);
        console.error("Error message:", error.message);
        setLoadingQueries(false);
      });

    // Cleanup function
    return () => {
      if (queriesUnsubscribeRef.current) {
        console.log("Cleaning up OSM queries listener");
        queriesUnsubscribeRef.current();
      }
    };
  }, [isAdmin]);

  useEffect(() => {
    if (!activeQueryId && osmQueries.length > 0) {
      const pendingQuery = osmQueries.find((query) => query.inserted_into_database !== true);
      if (pendingQuery) {
        setActiveQueryId(pendingQuery.id);
      }
    }
  }, [osmQueries, activeQueryId]);

  useEffect(() => {
    fetchRegions();
  }, [fetchRegions]);

  // Start listening to osm_collection_status for completion status
  const startProgressListener = async (userId, queryDocId) => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    setIsListening(true);
    setProgressCount(0);
    startTimeRef.current = Date.now();

    const statusDocRef = firestore
      .collection("osm_collection_status")
      .doc(userId);

    // Clear any existing status document to avoid stale data
    try {
      await statusDocRef.delete();
      console.log("Cleared old status document");
    } catch (error) {
      console.log("No existing status document to clear:", error);
    }

    // Listen to the status document for this user's OSM collection
    unsubscribeRef.current = statusDocRef
      .onSnapshot((doc) => {
        if (doc.exists) {
          const status = doc.data();
          console.log("OSM Status update:", status);

          if (queryDocId && status.query_id && status.query_id !== queryDocId) {
            console.log(
              `Ignoring status for query ${status.query_id} (active query ${queryDocId})`
            );
            return;
          }

          if (queryDocId && !status.query_id) {
            console.log("Status missing query_id, assuming stale and ignoring");
            return;
          }
          
          if (status.status === "running" && status.establishments_collected !== undefined) {
            setProgressCount(status.establishments_collected);
            // Update query with current count
            if (queryDocId) {
              firestore.collection("osm_queries").doc(queryDocId).update({
                number_of_results: status.establishments_collected
              }).catch(err => console.error("Error updating query count:", err));
            }
          } else if (status.status === "completed") {
            console.log("Function completed successfully");
            // Update query with final count
            if (queryDocId) {
              firestore.collection("osm_queries").doc(queryDocId).update({
                number_of_results: status.establishments_collected || 0
              }).catch(err => console.error("Error updating query final count:", err));
            }
            stopProgressListener();
            setResult({
              result: "success",
              message: status.message || `Successfully collected ${status.establishments_collected} establishments from OSM`,
              establishments_found: status.establishments_collected || 0,
              location: status.location,
              coordinates: status.coordinates,
              source: "status_document",
              completed_via: "cloud_function_status",
              environment: status.environment || "production",
              query_id: status.query_id || queryDocId
            });
          } else if (status.status === "failed") {
            console.log("Function failed:", status.error);
            stopProgressListener();
            setError(status.error || "OSM data collection failed");
          }
        }
      }, (error) => {
        console.error("Status listener error:", error);
        // Don't stop on listener errors - function might still be running
        console.log("Status listener failed, falling back to document count monitoring");
        startFallbackListener(userId, queryDocId);
      });
  };

  // Fallback to counting documents if status document fails
  const startFallbackListener = (userId, queryDocId) => {
    console.log("Starting fallback document count listener");
    
    unsubscribeRef.current = firestore
      .collection("raw_osm_data")
      .where("created_by_user", "==", userId)
      .onSnapshot((snapshot) => {
        const newCount = snapshot.docs.length;
        console.log(`OSM Progress (fallback): ${newCount} documents found`);
        setProgressCount(newCount);

        if (queryDocId) {
          firestore.collection("osm_queries").doc(queryDocId).update({
            number_of_results: newCount
          }).catch(err => console.error("Error updating query count (fallback):", err));
        }

        // Simple fallback: if we have documents and function timed out, 
        // assume completion after 2 minutes of no changes
        if (functionTimeout && newCount > 0) {
          const totalRunTime = Date.now() - startTimeRef.current;
          if (totalRunTime > 300000) { // 6 minutes
            console.log("Fallback completion detection triggered");
            stopProgressListener();
            setResult({
              result: "success",
              message: `Successfully collected ${newCount} establishments from OSM (fallback detection)`,
              establishments_found: newCount,
              source: "fallback_listener",
              completed_via: "timeout_fallback",
              environment: "production"
            });
          }
        }
      }, (error) => {
        console.error("Fallback listener error:", error);
        setError("Error monitoring progress");
        stopProgressListener();
      });
  };

  const stopProgressListener = () => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    setIsListening(false);
    setLoading(false);
  };

  const handleFetchOSMData = async () => {
    // Check for duplicate query first
    const normalizedLocation = location.trim().toLowerCase();
    const duplicate = osmQueries.find(query => {
      const queryLocation = (query.location || "").trim().toLowerCase();
      const queryRadius = query.radius_meters || 0;
      return queryLocation === normalizedLocation && queryRadius === radius;
    });

    if (duplicate) {
      setDuplicateQuery(duplicate);
      setShowDuplicateDialog(true);
      return;
    }

    // Any fresh fetch should reset push state and go back to step 1
    setPushComplete(false);
    setPushResult(null);
    setBarsToExclude(new Set());
    setActiveTab(0);

    // Show confirmation dialog if clearing existing data
    if (clearExistingData) {
      setShowConfirmDialog(true);
      return;
    }
    
    // Proceed with fetch
    await executeFetch();
  };

  const handleConfirmFetch = async () => {
    setShowConfirmDialog(false);
    await executeFetch();
  };

  const handleCancelFetch = () => {
    setShowConfirmDialog(false);
  };

  const handleCancelDuplicate = () => {
    setShowDuplicateDialog(false);
    setDuplicateQuery(null);
  };

  const handleContinueDuplicate = async () => {
    setShowDuplicateDialog(false);
    setDuplicateQuery(null);

    // Show confirmation dialog if clearing existing data
    if (clearExistingData) {
      setShowConfirmDialog(true);
      return;
    }
    
    // Proceed with fetch
    await executeFetch();
  };

  const executeFetch = async () => {
    // Prevent multiple simultaneous runs
    if (loading) {
      console.log("Function already running, ignoring duplicate request");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setFunctionTimeout(false);
    setPushComplete(false);
    setPushResult(null);

    try {
      // Start listening for progress using the current Firebase Auth UID
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError("User not authenticated");
        setLoading(false);
        return;
      }
      
      // Save query to osm_queries collection
      const queryDoc = await firestore.collection("osm_queries").add({
        location: location.trim(),
        radius_meters: radius,
        query_date: new Date(),
        number_of_results: 0,
        inserted_into_database: false,
        inserted_date: null,
        created_by_user: currentUser.uid,
        created_by_email: currentUser.email || null
      });
      
      console.log("Created query record:", queryDoc.id);
      setActiveQueryId(queryDoc.id);
      
      await startProgressListener(currentUser.uid, queryDoc.id);

      const payload = {
        location: location.trim(),
        radiusMeters: radius,
        clearExistingData: clearExistingData,
        queryId: queryDoc.id
      };

      console.log("Fetching OSM data with payload:", payload);
      
      // Set up a timeout for the function call (60 seconds)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Function call timeout")), 60000);
      });

      try {
        const response = await Promise.race([
          osmDataFunction(payload),
          timeoutPromise
        ]);
        
        // Function completed successfully before timeout
        console.log("OSM data response:", response);
        setResult(response.data);
        stopProgressListener();
        
      } catch (functionError) {
        if (functionError.message === "Function call timeout") {
          console.log("Function call timed out, but listening for progress...");
          setFunctionTimeout(true);
          // Keep listening - don't stop here
        } else {
          console.error("Function error:", functionError);
          setError(functionError.message || "Failed to fetch OSM data");
          stopProgressListener();
        }
      }
      
    } catch (err) {
      console.error("OSM fetch error:", err);
      setError(err.message || "Failed to fetch OSM data");
      stopProgressListener();
    }
  };

  const isFormValid = () => {
    return location.trim().length > 0 && radius > 0 && radius <= 50000;
  };

  const formatDistance = (meters) => {
    if (!Number.isFinite(meters)) {
      return "—";
    }
    const miles = meters / METERS_IN_MILE;
    const milesDisplay = miles >= 10 ? miles.toFixed(1) : miles.toFixed(2);
    return `${milesDisplay} mi`;
  };

  const handleRadiusMilesChange = (miles) => {
    const numericMiles = Number.isFinite(miles)
      ? miles
      : Number((DEFAULT_RADIUS_METERS / METERS_IN_MILE).toFixed(2));

    const meters = Math.max(
      MIN_RADIUS_METERS,
      Math.min(MAX_RADIUS_METERS, Math.round(numericMiles * METERS_IN_MILE))
    );

    setRadius(meters);
  };

  const buildBlacklistPayload = (bars) => {
    const seen = new Set();
    return bars.reduce((acc, bar) => {
      const name = (bar.bar_name || bar.name || bar.osm_metadata?.name || "").trim();
      const address = (
        bar.bar_address ||
        bar.address ||
        bar.osm_metadata?.display_name ||
        bar.osm_metadata?.address?.formatted ||
        bar.osm_metadata?.address?.display_name ||
        bar.osm_metadata?.address ||
        ""
      ).trim();

      if (!name || !address) {
        return acc;
      }

      const key = `${name.toLowerCase()}|${address.toLowerCase()}`;
      if (seen.has(key)) {
        return acc;
      }
      seen.add(key);
      acc.push({ name, address });
      return acc;
    }, []);
  };

  // Checkbox handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = new Set(osmRawData.map(item => item.id));
      setSelectedRows(allIds);
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleDeleteSelected = async () => {
    setShowDeleteConfirm(false);

    const barsBeingDeleted = osmRawData.filter((bar) => selectedRows.has(bar.id));

    if (!skipBlacklistOnDelete && barsBeingDeleted.length) {
      try {
        const blacklistPayload = buildBlacklistPayload(barsBeingDeleted);
        if (blacklistPayload.length) {
          await addToBlacklistFunction({ bars: blacklistPayload });
          console.log(`Added ${blacklistPayload.length} bars to blacklist before deletion`);
        }
      } catch (blacklistError) {
        console.error("Error adding bars to blacklist:", blacklistError);
        setError("Failed to add bars to the blacklist. The rows were still removed.");
      }
    }

    try {
      const batch = firestore.batch();
      selectedRows.forEach(id => {
        batch.delete(firestore.collection("raw_osm_data").doc(id));
      });
      
      await batch.commit();
      console.log(`Deleted ${selectedRows.size} records`);
      setSelectedRows(new Set());
      setSkipBlacklistOnDelete(false);
    } catch (error) {
      console.error("Error deleting records:", error);
      setError(`Failed to delete records: ${error.message}`);
    }
  };

  // Detect conflicts when switching to conflict resolution tab
  useEffect(() => {
    if (activeTab === 1 && osmRawData.length > 0) {
      detectConflicts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, osmRawData.length]); // Only depend on tab and data length to avoid infinite loops

  const detectConflicts = async () => {
    if (osmRawData.length === 0) {
      console.log("No OSM data to check for conflicts");
      return;
    }

    setLoadingConflicts(true);
    setError(null); // Clear any previous errors
    setConflictsError(null);
    
    try {
      console.log(`Detecting conflicts for ${osmRawData.length} OSM records`);
      
      // Call Cloud Function to detect conflicts
      // Function will fetch OSM data from Firestore and compare with Supabase temp_bars
      const response = await fetchConflictsFunction();

      console.log("Conflict detection response:", response.data);

      const conflictsData = response.data.conflicts || [];
      
      setConflicts(conflictsData);
      
      // Initialize field selections - default to 'new' for non-conflicting fields
      const initialSelections = {};
      conflictsData.forEach(conflict => {
        initialSelections[conflict.id] = {};
        Object.keys(conflict.fields).forEach(field => {
          const fieldData = conflict.fields[field];
          if (!fieldData.hasConflict) {
            // Auto-select the value that exists
            initialSelections[conflict.id][field] = fieldData.new ? "new" : "existing";
          }
        });
      });
      setFieldSelections(initialSelections);
      
      console.log(`Found ${conflictsData.length} conflicts`);
    } catch (error) {
      console.error("Error detecting conflicts:", error);
      setError(`Failed to detect conflicts: ${error.message}`);
      setConflictsError(error.message || "Conflict detection failed");
      setConflicts([]);
    } finally {
      setLoadingConflicts(false);
    }
  };

  const handleFieldSelection = (conflictId, fieldName, source) => {
    setFieldSelections(prev => ({
      ...prev,
      [conflictId]: {
        ...prev[conflictId],
        [fieldName]: source
      }
    }));
  };

  const hasUnresolvedConflicts = () => {
    const unresolvedList = [];
    conflicts.forEach(conflict => {
      Object.keys(conflict.fields).forEach(fieldName => {
        const field = conflict.fields[fieldName];
        if (field.hasConflict && !fieldSelections[conflict.id]?.[fieldName]) {
          unresolvedList.push({
            conflictId: conflict.id,
            fieldName,
            hasSelection: !!fieldSelections[conflict.id]?.[fieldName]
          });
        }
      });
    });
    
    if (unresolvedList.length > 0) {
      console.log("Unresolved conflicts:", unresolvedList);
      console.log("Current selections:", fieldSelections);
    }
    
    return unresolvedList.length > 0;
  };

  const toggleBarExclusion = (osmRecordId) => {
    setBarsToExclude(prev => {
      const newSet = new Set(prev);
      if (newSet.has(osmRecordId)) {
        newSet.delete(osmRecordId);
      } else {
        newSet.add(osmRecordId);
      }
      return newSet;
    });
  };

  const handleIncludeAllBars = () => {
    setBarsToExclude(new Set());
  };

  const handleExcludeAllBars = () => {
    setBarsToExclude(new Set(filteredOsmRawData.map((r) => r.id)));
  };

  const handleAssignRegionChange = (event) => {
    const regionId = event.target.value;
    setAssignRegionId(regionId);
    setOsmRawData((current) => current.map((bar) => {
      const nextMetadata = { ...(bar.osm_metadata || {}) };
      if (regionId) {
        nextMetadata.region_id = regionId;
      } else {
        delete nextMetadata.region_id;
      }
      const nextBar = {
        ...bar,
        regionId: regionId || undefined,
        osm_metadata: nextMetadata
      };
      return nextBar;
    }));
  };

  const handlePushToDatabase = async () => {
    setPushingToDatabase(true);
    setPushResult(null);

    const isDevProject = firebaseProjectId === "barhopper-269017";
    // Avoid hitting the push function when running the web app locally/dev or on the dev project
    if (process.env.NODE_ENV === "development" || isDevProject) {
      setPushResult({
        success: false,
        message: "Push to database is disabled in development/dev project."
      });
      setPushingToDatabase(false);
      return;
    }
    
    try {
      const sourceBars = addUnlistedOnly
        ? osmRawData.filter((bar) => !conflictIdSet.has(bar.id))
        : osmRawData;

      // Prepare bars data with conflict resolutions applied
      const barsToUpload = sourceBars
        .filter((bar) => !barsToExclude.has(bar.id))
        .map(bar => {
        // Check if this bar has conflicts
        const conflict = conflicts.find(c => c.osmRecordId === bar.id);
        
        if (conflict) {
          // Apply field selections from conflict resolution
          const resolvedBar = { ...bar };
          Object.keys(conflict.fields).forEach(fieldName => {
            const field = conflict.fields[fieldName];
            const selection = fieldSelections[conflict.id]?.[fieldName];
            
            if (selection === "existing") {
              // Use existing DB value
              resolvedBar[fieldName] = field.existing;
            } else if (selection === "new" || !field.hasConflict) {
              // Use new OSM value (default for non-conflicts)
              resolvedBar[fieldName] = field.new;
            }
          });

          if (conflict.existingRecordId) {
            // Preserve the canonical Firebase bar id when merging into an existing bar
            resolvedBar.existing_bar_id = conflict.existingRecordId;
            resolvedBar.bar_id = conflict.existingRecordId;
          }

          return {
            ...resolvedBar,
            original_osm_bar_id: bar.bar_id,
            firestoreDocId: bar.id, // Store original Firestore doc ID for cleanup
            source: "osm_with_conflict_resolution"
          };
        }
        
        // No conflict, return as-is
        return {
          ...bar,
          original_osm_bar_id: bar.bar_id,
          firestoreDocId: bar.id,
          source: "osm"
        };
      });

      // Optionally blacklist excluded bars so they do not reappear on future fetches
      if (blacklistUnincluded) {
        try {
          const excludedBars = sourceBars.filter((bar) => barsToExclude.has(bar.id));
          const blacklistPayload = buildBlacklistPayload(excludedBars);
          if (blacklistPayload.length) {
            await addToBlacklistFunction({ bars: blacklistPayload });
            console.log(`Blacklisted ${blacklistPayload.length} excluded bars before push`);
          }
        } catch (blacklistError) {
          console.error("Error blacklisting excluded bars:", blacklistError);
          setError("Failed to add excluded bars to the blacklist. Push will continue.");
        }
      }
      
      console.log("Pushing bars to database:", {
        total: barsToUpload.length,
        excluded: Array.from(barsToExclude)
      });
      
      const barsWithRegion = barsToUpload.map((bar) => {
        const regionId = bar.regionId || assignRegionId || bar.osm_metadata?.region_id;
        if (!regionId) {
          return bar;
        }
        return {
          ...bar,
          regionId,
          osm_metadata: {
            ...(bar.osm_metadata || {}),
            region_id: regionId
          }
        };
      });

      // Call Cloud Function to push bars
      const response = await pushBarsFunction({
        barsData: barsWithRegion,
        excludedBarIds: Array.from(barsToExclude)
      });
      
      console.log("Push response:", response.data);
      
      if (response.data) {
        const status = response.data.status || (response.data.errors > 0
          ? (response.data.inserted > 0 ? "partial" : "failed")
          : "success");

        const success = status === "success";

        setPushResult({
          success,
          status,
          recordsProcessed: response.data.inserted,
          errors: response.data.errors,
          excluded: response.data.excluded,
          errorDetails: response.data.errorDetails,
          message: response.data.message
        });
        setPushComplete(true);

        if (activeQueryId) {
          try {
            await firestore.collection("osm_queries").doc(activeQueryId).set(
              {
                inserted_into_database: true,
                inserted_date: new Date(),
                records_inserted: response.data.inserted || 0,
                excluded_records: response.data.excluded || 0
              },
              { merge: true }
            );
          } catch (updateError) {
            console.error("Failed to mark OSM query as inserted", updateError);
          }
        }
        
        // Clear local state after successful push
        setOsmRawData([]);
        setConflicts([]);
        setFieldSelections({});
        setBarsToExclude(new Set());
        setActiveQueryId(null);
      }
      
    } catch (error) {
      console.error("Error pushing to database:", error);
      setPushResult({
        success: false,
        status: "failed",
        error: error.message,
        message: `Failed to push bars to database: ${error.message}`
      });
      setPushComplete(true);
    } finally {
      setPushingToDatabase(false);
    }
  };

  const unresolvedConflicts = activeTab === 1 && conflicts.length > 0 ? hasUnresolvedConflicts() : false;
  const readyToUploadCount = filteredOsmRawData.filter((bar) => !barsToExclude.has(bar.id)).length;
  const effectiveExcludedCount = filteredOsmRawData.reduce(
    (total, bar) => total + (barsToExclude.has(bar.id) ? 1 : 0),
    0
  );
  const hasRegionSelected = Boolean(assignRegionId);

  return (
    <Container>
      <Typography variant="h4" gutterBottom color="primary">
        <Map sx={{ mr: 1, verticalAlign: "middle" }} />
        OSM Data Collection Admin
      </Typography>
      
      <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
        Mass import bars from OpenStreetMap in three simple steps: collect data, resolve conflicts, and push to database.
      </Typography>

      {/* OSM Query History - Instructions */}
      <QueryHistoryCard
        osmQueries={osmQueries}
        loadingQueries={loadingQueries}
        formatDistance={formatDistance}
      />

      {/* Roadmap Steps */}
      <Box sx={{ mb: 3, mt: 4 }}>
        <Stepper activeStep={activeTab} alternativeLabel>
          <Step>
            <StepLabel>Collect OSM Data</StepLabel>
          </Step>
          <Step>
            <StepLabel>Resolve Conflicts</StepLabel>
          </Step>
          <Step>
            <StepLabel>Push to Database</StepLabel>
          </Step>
        </Stepper>
      </Box>

      {/* Tabs Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
        >
          <Tab label="1. Collect OSM Data" />
          <Tab label="2. Resolve Conflicts" disabled={osmRawData.length === 0} />
          <Tab label="3. Push to Database" disabled={osmRawData.length === 0} />
        </Tabs>
      </Box>

      {/* Tab 0: Collect OSM Data */}
      {activeTab === 0 && (
        <>
          <DataCollectionInfo />

          <SearchConfigurationCard
            location={location}
            onLocationChange={setLocation}
            radiusMiles={radiusMiles}
            radiusMeters={radius}
            onRadiusMilesChange={handleRadiusMilesChange}
            clearExistingData={clearExistingData}
            onToggleClearExistingData={setClearExistingData}
            onFetch={handleFetchOSMData}
            isFormValid={isFormValid()}
            loading={loading}
            isListening={isListening}
            functionTimeout={functionTimeout}
            progressCount={progressCount}
            formatDistance={formatDistance}
          />

          <FetchProgressAlert visible={functionTimeout || isListening} progressCount={progressCount} />

          <FetchErrorAlert error={error} />

          <CollectionResultsCard result={result} />

          {/* OSM Raw Data Display */}
          <CollectedDataCard
            osmRawData={osmRawData}
            selectedRows={selectedRows}
            onSelectAll={handleSelectAll}
            onSelectRow={handleSelectRow}
            onRequestDelete={() => {
              setSkipBlacklistOnDelete(false);
              setShowDeleteConfirm(true);
            }}
            onRefresh={() => window.location.reload()}
            loadingRawData={loadingRawData}
          />
        </>
      )}

      {/* Tab 2: Resolve Conflicts */}
      {activeTab === 1 && (
        <>
          <ConflictResolutionAlert />

              {loadingConflicts ? (
            <Box display="flex" alignItems="center" gap={2} justifyContent="center" py={4}>
              <CircularProgress size={30} />
              <Typography>Detecting conflicts with existing database records...</Typography>
            </Box>
              ) : conflictsError ? (
                <Alert severity="error">
                  <Typography variant="body1">
                    <strong>Conflict detection failed.</strong> {conflictsError}
                  </Typography>
                </Alert>
              ) : conflicts.length === 0 ? (
                <Alert severity="success">
                  <Typography variant="body1">
                    <strong>No conflicts detected!</strong> All OSM data is unique and ready to be pushed to the database.
                  </Typography>
                </Alert>
          ) : (
            <>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2} flexWrap="wrap">
                <ConflictsSummary
                  totalConflicts={conflicts.length}
                  hasUnresolvedConflicts={unresolvedConflicts}
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={addUnlistedOnly}
                      onChange={(e) => setAddUnlistedOnly(e.target.checked)}
                      color="primary"
                    />
                  )}
                  label="Add Unlisted Bars Only"
                />
              </Box>

              {addUnlistedOnly && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Bars with conflicts are excluded from upload and hidden from the review table. Only unlisted bars will be added.
                </Alert>
              )}

              <ConflictsTable
                conflicts={conflicts}
                fieldSelections={fieldSelections}
                onFieldSelection={handleFieldSelection}
              />

              <ConflictActions
                onBack={() => setActiveTab(0)}
                onProceed={() => setActiveTab(2)}
                disableProceed={unresolvedConflicts}
              />
            </>
          )}
        </>
      )}

      {/* Tab 3: Push to Database */}
      {activeTab === 2 && (
        <>
          <PushInfoAlert />

          {pushComplete && pushResult ? (
            <PushCompleteAlert
              message={pushResult.message}
              status={pushResult.status || (pushResult.success ? "success" : "failed")}
              errors={pushResult.errors}
              recordsProcessed={pushResult.recordsProcessed}
              onReturn={() => setActiveTab(0)}
            />
          ) : (
            <>
              <UploadSummaryCard
                totalRecords={filteredOsmRawData.length}
                conflictsResolved={conflicts.length}
                excludedCount={effectiveExcludedCount}
              />

              <BarsReviewTable
                osmRawData={filteredOsmRawData}
                barsToExclude={barsToExclude}
                conflicts={conflicts}
                onToggleBarExclusion={toggleBarExclusion}
                onIncludeAll={handleIncludeAllBars}
                onExcludeAll={handleExcludeAllBars}
                regions={regions}
                assignRegionId={assignRegionId}
                onAssignRegionChange={handleAssignRegionChange}
                disableRegionSelect={pushingToDatabase || osmRawData.length === 0}
                blacklistUnincluded={blacklistUnincluded}
                onToggleBlacklistUnincluded={setBlacklistUnincluded}
              />

              <PushActions
                onBack={() => setActiveTab(1)}
                onPush={handlePushToDatabase}
                pushing={pushingToDatabase}
                readyCount={readyToUploadCount}
                disabled={pushingToDatabase || readyToUploadCount === 0 || !hasRegionSelected}
                errorMessage={pushResult && !pushResult.success ? pushResult.message : null}
              />
            </>
          )}
        </>
      )}

      <ClearDataDialog
        open={showConfirmDialog}
        onCancel={handleCancelFetch}
        onConfirm={handleConfirmFetch}
      />

      <DeleteRowsDialog
        open={showDeleteConfirm}
        selectedCount={selectedRows.size}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteSelected}
        blacklistOptOutChecked={skipBlacklistOnDelete}
        onToggleBlacklistOptOut={setSkipBlacklistOnDelete}
      />

      {/* Duplicate Query Dialog */}
      {showDuplicateDialog && duplicateQuery && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999
          }}
        >
          <Box
            sx={{
              bgcolor: "background.paper",
              borderRadius: 2,
              p: 3,
              maxWidth: 500,
              width: "90%",
              boxShadow: 24
            }}
          >
            <Typography variant="h6" gutterBottom color="warning.main">
              Duplicate Query Detected
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                A similar query has already been performed:
              </Typography>
              <Box sx={{ mt: 1, pl: 2 }}>
                <Typography variant="body2">
                  <strong>Location:</strong> {duplicateQuery.location}
                </Typography>
                <Typography variant="body2">
                  <strong>Radius:</strong> {formatDistance(duplicateQuery.radius_meters)}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong>{" "}
                  {duplicateQuery.query_date?.toDate
                    ? duplicateQuery.query_date.toDate().toLocaleString()
                    : "Unknown"}
                </Typography>
                <Typography variant="body2">
                  <strong>Results:</strong> {duplicateQuery.number_of_results || 0} bars
                </Typography>
                {duplicateQuery.inserted_into_database && (
                  <Typography variant="body2" color="success.main">
                    ✓ Already inserted into database
                  </Typography>
                )}
              </Box>
            </Alert>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Running this query again may produce duplicate data. Are you sure you want to continue?
            </Typography>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <button
                onClick={handleCancelDuplicate}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  background: "white",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleContinueDuplicate}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "4px",
                  background: "#f57c00",
                  color: "white",
                  cursor: "pointer"
                }}
              >
                Continue Anyway
              </button>
            </Box>
          </Box>
        </Box>
      )}
    </Container>
  );
}

export default OSMDataAdmin;